import { Box, Tabs, Tab } from "@mui/material";
import VampInfoButton from "../components/dashboard/vampRates/VampInfoButton";
import { useSearchParams } from "react-router";
import PageMeta from "../components/common/PageMeta";
import { useStateValue } from "../context/StateValueContext";
import { hasPermission } from "../functions/permissionUtils";
import { PERMISSIONS } from "../constants/permissions";
import { useVampRatesVisible } from "../hooks/useVampRatesAccess";
import OverviewTab from "./Dashboard/OverviewTab";
import DeflectionsTab from "./Dashboard/DeflectionsTab";
import VampRatesTab from "./Dashboard/VampRatesTab";

export type { Currency, DashboardTab } from "./Dashboard/OverviewTab";
export { CURRENCY_STORAGE_KEY, ASSIGNED_ONLY_STORAGE_KEY } from "./Dashboard/OverviewTab";

type DashboardView = "overview" | "deflections" | "vamp";

export default function Dashboard() {
  const [{ user }] = useStateValue();
  const [searchParams, setSearchParams] = useSearchParams();
  const canSeeDeflections = hasPermission(user, PERMISSIONS.DASHBOARD);
  const canSeeVamp = useVampRatesVisible();

  const views: { value: DashboardView; allowed?: boolean }[] = [
    { value: "overview" },
    { value: "deflections", allowed: canSeeDeflections },
    { value: "vamp", allowed: canSeeVamp },
  ];

  const requestedView = searchParams.get("view");
  const matched = views.find((v) => v.value === requestedView);
  const view: DashboardView =
    matched && matched.allowed === true ? matched.value : "overview";

  const handleChange = (_: React.SyntheticEvent, next: string) => {
    setSearchParams((prev) => {
      prev.set("view", next);
      return prev;
    });
  };

  return (
    <>
      <PageMeta title="Chargemont | Dashboard" description="Chargemont | Dashboard" />
      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] xl:p-10">
        <h3 className="mb-4 font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl">
          Dashboard
        </h3>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            borderBottom: "1px solid #e4e7ec",
          }}
        >
          <Tabs
            value={view}
            onChange={handleChange}
            sx={{
              flexGrow: 1,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
              "& .MuiTabs-indicator": { backgroundColor: "#465fff" },
            }}
          >
            <Tab label="Overview" value="overview" />
            {canSeeDeflections && <Tab label="Deflections" value="deflections" />}
            {canSeeVamp && <Tab label="VAMP rates" value="vamp" />}
          </Tabs>

          {view === "vamp" && <VampInfoButton />}
        </Box>

        {view === "overview" && <OverviewTab />}
        {view === "deflections" && <DeflectionsTab />}
        {view === "vamp" && <VampRatesTab />}
      </div>
    </>
  );
}
